import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ImageUploaderComponent } from '../../../../../shared/image-uploader/image-uploader.component';
import { ToastNotificationService } from '../../../../../notifications/services/toast-notification.service';
import { ToastNotificationType, ToastPosition } from '../../../../../notifications/models/toast-notification-type';
import { UsersManagementApiService } from '../../../services/users-management-api.service';
import { CreateStaffPayload, UpdateStaffPayload } from '../../../models/staff-payload';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../../shared/services/translations/language.service';
import { ApiErrorService } from '../../../../../shared/services/api-error.service';

@Component({
  selector: 'app-staff-form-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, ImageUploaderComponent, TranslateModule],
  templateUrl: './staff-form-page.component.html',
  styleUrl: './staff-form-page.component.css'
})
export class StaffFormPageComponent implements OnInit {

  private apiService = inject(UsersManagementApiService);
  private apiErrorService = inject(ApiErrorService);
  private toast = inject(ToastNotificationService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private translate = inject(TranslateService);
  languageService = inject(LanguageService);

  staffId: string | null = null;
  disableButtons = false;
  loadingExisting = false;
  submitLoading = false;
  fieldErrors: Record<string, string[]> = {};
  showPassword = false;

  staffRoles: { label: string; value: string }[] = [
    { label: 'users_management.roles.owner', value: 'owner' },
    { label: 'users_management.roles.admin', value: 'admin' },
  ];

  get isRTL(): boolean {
    return this.languageService.currentLang() === 'ar';
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  form: CreateStaffPayload = {
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    first_name_ar: '',
    last_name_ar: '',
    phone_number: '',
    staff_role: 'admin',
    profile_image: null,
  };

  get isEditMode(): boolean { return this.staffId !== null; }

  ngOnInit(): void {
    this.staffId = this.route.snapshot.paramMap.get('id');
    if (this.staffId) this.loadExisting(this.staffId);
  }

  private loadExisting(id: string): void {
    this.loadingExisting = true;
    this.apiService.getVendorStaffById(id).subscribe({
      next: (staff) => {
        this.form = {
          email: staff.user.email ?? '',
          password: '',
          first_name: staff.user.first_name ?? '',
          last_name: staff.user.last_name ?? '',
          first_name_ar: staff.user.first_name_ar ?? '',
          last_name_ar: staff.user.last_name_ar ?? '',
          phone_number: staff.user.phone_number ?? '',
          staff_role: staff.role ?? 'admin',
          profile_image: staff.user.profile_image ?? null,
        };
        this.loadingExisting = false;
      },
      error: () => {
        this.loadingExisting = false;
        this.toast.createNotification(this.translate.instant('users_management.form.errors.load_message'), ToastNotificationType.ERROR, this.translate.instant('users_management.form.errors.load_title'), ToastPosition.TOP_RIGHT);
      }
    });
  }

  onProfileImageChange(images: string[]): void {
    this.form.profile_image = images[0] ?? null;
  }

  submit(ngForm: NgForm): void {
    if (ngForm.invalid) {
      ngForm.control.markAllAsTouched();
      this.toast.createNotification(this.translate.instant('users_management.form.errors.missing_fields'), ToastNotificationType.ERROR, "", ToastPosition.TOP_RIGHT);
      return;
    }

    this.fieldErrors = {};
    this.disableButtons = true;
    this.submitLoading = true;

    if (this.isEditMode && this.staffId) {
      const payload: UpdateStaffPayload = { ...this.form };
      if (!payload.password) delete payload.password;
      this.apiService.updateVendorStaff(this.staffId, payload).subscribe({
        next: () => {
          this.disableButtons = false;
          this.submitLoading = false;
          this.toast.createNotification(this.translate.instant('users_management.form.success.saved_message'), ToastNotificationType.SUCCESS, this.translate.instant('users_management.form.success.saved_title'), ToastPosition.TOP_RIGHT);
          this.router.navigate(['/users']);
        },
        error: (err) => {
          this.disableButtons = false;
          this.submitLoading = false;
          this.applyApiErrors(err, this.translate.instant('users_management.form.errors.save_failed'));
        }
      });
    } else {
      this.apiService.createVendorStaff(this.form).subscribe({
        next: () => {
          this.disableButtons = false;
          this.submitLoading = false;
          this.toast.createNotification(this.translate.instant('users_management.form.success.created_message'), ToastNotificationType.SUCCESS, this.translate.instant('users_management.form.success.created_title'), ToastPosition.TOP_RIGHT);
          this.router.navigate(['/users']);
        },
        error: (err) => {
          this.disableButtons = false;
          this.submitLoading = false;
          this.applyApiErrors(err, this.translate.instant('users_management.form.errors.create_failed'));
        }
      });
    }
  }

  private applyApiErrors(err: any, fallbackMessage: string): void {
    const fieldErrors = this.apiErrorService.getFieldErrors(err);
    if (Object.keys(fieldErrors).length > 0) {
      this.fieldErrors = fieldErrors;
      return;
    }
    this.toast.createNotification(this.apiErrorService.getDisplayMessage(err, fallbackMessage), ToastNotificationType.ERROR);
  }

  clearFieldError(field: string): void {
    if (this.fieldErrors[field]) {
      const { [field]: _, ...rest } = this.fieldErrors;
      this.fieldErrors = rest;
    }
  }

  cancel(): void {
    this.router.navigate(['/users']);
  }
}
